import type { MouseEvent, ReactNode } from "react";
import { LabLink } from "../chrome";
import {
  LAB_BRAND,
  LAB_CTA,
  LAB_EXERCISE,
  LAB_HEADLINE,
  LAB_LEAD,
  LAB_OTHER,
  LAB_PRICE,
  LAB_SECONDARY,
  LAB_SELLER,
  LAB_TRAINERS,
  LAB_TRUST,
} from "../copy";
import { useFanLesson } from "../lesson";
import "../../styles/lab-pocket.css";

type LabScreen = "landing" | "exercise";

export const POCKET_ID = "pocket" as const;
export const POCKET_NAME = "Карман";

type Traverse = (href: string, event: MouseEvent<HTMLAnchorElement>) => void;

function Phone({
  basePath,
  hrefFor,
  onTraverse,
  children,
}: {
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
  children: ReactNode;
}) {
  return (
    <div className="pocket-desk">
      <div className="pocket-phone">
        <header className="pocket-head">
          <LabLink className="pocket-brand" href={hrefFor("landing")} onTraverse={onTraverse}>
            {LAB_BRAND}
          </LabLink>
          <LabLink className="pocket-switch" href={basePath} onTraverse={onTraverse}>
            {LAB_OTHER}
          </LabLink>
        </header>
        {children}
        <span className="pocket-home" aria-hidden="true" />
      </div>
    </div>
  );
}

export function PocketPoster(props: { href: string; onTraverse?: Traverse }) {
  return (
    <LabLink className="pocket-poster" href={props.href} onTraverse={props.onTraverse}>
      <div className="pocket-poster-wide" aria-hidden="true">
        <div className="pocket-poster-phone">
          <p className="pocket-poster-brand">{LAB_BRAND}</p>
          <p className="pocket-poster-h">
            Три тренажёра —
            <br />
            один живой английский
          </p>
          <ul>
            <li>
              <span>🔊</span> Английские звуки
            </li>
            <li>
              <span>🎬</span> Слово в кадре
            </li>
            <li>
              <span>🎭</span> Фразовый режиссёр
            </li>
          </ul>
          <span className="pocket-poster-cta">{LAB_CTA}</span>
        </div>
      </div>
      <div className="pocket-poster-slim" aria-hidden="true">
        <p className="pocket-poster-brand">{LAB_BRAND}</p>
        <p className="pocket-poster-h">Три тренажёра — один живой английский</p>
        <div className="pocket-poster-tiles">
          <span>🔊</span>
          <span>🎬</span>
          <span>🎭</span>
        </div>
        <span className="pocket-poster-cta">{LAB_CTA}</span>
      </div>
      <span className="sr-only">
        {POCKET_NAME}. {LAB_HEADLINE}
      </span>
    </LabLink>
  );
}

export function PocketWorld(props: {
  screen: LabScreen;
  basePath: string;
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <div data-world={POCKET_ID} data-lab-screen={props.screen} className="pocket-world">
      <Phone basePath={props.basePath} hrefFor={props.hrefFor} onTraverse={props.onTraverse}>
        {props.screen === "exercise" ? (
          <PocketExercise />
        ) : (
          <PocketLanding hrefFor={props.hrefFor} onTraverse={props.onTraverse} />
        )}
      </Phone>
    </div>
  );
}

function PocketLanding({
  hrefFor,
  onTraverse,
}: {
  hrefFor: (screen: LabScreen) => string;
  onTraverse?: Traverse;
}) {
  return (
    <main className="pocket-landing">
      <h1>
        Три тренажёра —
        <br />
        один живой английский
      </h1>
      <p className="pocket-lead">{LAB_LEAD}</p>
      <ul className="pocket-tiles">
        {LAB_TRAINERS.map((trainer) => (
          <li key={trainer.title}>
            <LabLink href={hrefFor("exercise")} onTraverse={onTraverse}>
              <span aria-hidden="true">{trainer.emoji}</span>
              <strong>{trainer.title}</strong>
              <em>{trainer.goal}</em>
            </LabLink>
          </li>
        ))}
      </ul>
      <LabLink className="pocket-cta" href={hrefFor("exercise")} onTraverse={onTraverse}>
        {LAB_CTA}
      </LabLink>
      <LabLink className="pocket-text" href={hrefFor("exercise")} onTraverse={onTraverse}>
        {LAB_SECONDARY}
      </LabLink>
      <p className="pocket-trust">{LAB_TRUST}</p>
      <p className="pocket-meta">
        {LAB_SELLER.name}
        <br />
        {LAB_PRICE}
      </p>
    </main>
  );
}

function PocketExercise() {
  const lesson = useFanLesson();
  return (
    <main className="pocket-lesson" data-play={lesson.play}>
      <p className="pocket-kicker">{LAB_EXERCISE.kicker}</p>
      <h1>{LAB_EXERCISE.title}</h1>
      <p className="pocket-lead">{LAB_EXERCISE.lead}</p>
      <div className="pocket-word">
        <span aria-hidden="true">💨</span>
        <strong>{LAB_EXERCISE.word}</strong>
        <em>{LAB_EXERCISE.gloss}</em>
        <p>{LAB_EXERCISE.example}</p>
      </div>
      <p className="pocket-status" role="status">
        {LAB_EXERCISE.status[lesson.play]}
      </p>
      <div className="pocket-tools">
        <button type="button" className={lesson.play === "listen" ? "pocket-cta" : "pocket-ghost"} onClick={lesson.listen}>
          {LAB_EXERCISE.listen}
        </button>
        <button type="button" className={lesson.play === "speak" ? "pocket-cta" : "pocket-ghost"} onClick={lesson.speak}>
          {LAB_EXERCISE.speak}
        </button>
        <button
          type="button"
          className={lesson.play === "done" ? "pocket-cta" : "pocket-ghost"}
          disabled={!lesson.ready}
          onClick={lesson.finish}
        >
          {LAB_EXERCISE.done}
        </button>
      </div>
    </main>
  );
}
